import React, { useState } from "react";
import { Box, CircularProgress, Snackbar, Alert } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { format } from "date-fns";
import { useLoaderData, useRevalidator } from "react-router-dom";
import type { LoaderFunctionArgs } from "react-router-dom";

import { DynamicTable, TableColumn } from "../../components/common/table/Table";
import {
  fetchAllGlassTypes,
  fetchLastMaterialGlassByStationId,
} from "../../services/glassLoaderService";
import { GlassType, MaterialGlassType } from "../../types/GlassLoaderTypes";
import { GlassLoaderForm } from "../../components/common/form/GlassLoaderForm";

export async function loader(_: LoaderFunctionArgs) {
  const stationId = 54;
  const [glassType, materialGlass] = await Promise.all([
    fetchAllGlassTypes(stationId),
    fetchLastMaterialGlassByStationId(stationId),
  ]);
  return {
    title: "Glass Loader 1 Frontglas",
    glassType,
    materialGlass,
    stationId,
  };
}

export default function GlassLoaderOnePage() {
  const methods = useForm();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loaderData = useLoaderData() as {
    glassType: GlassType[];
    materialGlass: MaterialGlassType[];
  };
  const revalidator = useRevalidator();
  const isReloading = revalidator.state === "loading";

  const handleSuccess = () => {
    setShowSuccess(true);
    revalidator.revalidate();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setShowSnackbar(true);
  };

  const handleSnackbarClose = (
    _: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShowSnackbar(false);
  };

  const lastCategory =
    loaderData.materialGlass.find(
      (e) => (e as any)?.glassType || (e as any)?.glasstype
    )?.glassType ||
    (loaderData.materialGlass.find((e) => (e as any)?.glasstype) as any)
      ?.glasstype;
  const stationId =
    lastCategory && String(lastCategory).toLowerCase() === "frontglass"
      ? 54
      : 19;

  return (
    <Box p={4}>
      <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start">
        {/* Form Side */}
        <Box flex={1}>
          <FormProvider {...methods}>
            <GlassLoaderForm
              onSuccess={handleSuccess}
              onError={handleError}
              glassTypes={loaderData.glassType}
              stationId={stationId}
            />
          </FormProvider>
        </Box>

        {/* Table Side */}
        <Box flex={3}>
          {isReloading ? (
            <CircularProgress />
          ) : (
            <DynamicTable columns={columns} data={loaderData.materialGlass} />
          )}
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Glas erfolgreich gespeichert!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error || "Ein Fehler ist aufgetreten"}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const columns: TableColumn<MaterialGlassType>[] = [
  {
    key: "dateinproduction",
    label: "Produktionsdatum",
    render: (value, _row) =>
      value ? format(new Date(value as string), "yyyy-MM-dd HH:mm:ss") : "-",
  },
  {
    key: "firstname",
    label: "Operator",
    render: (_value, row) =>
      [(row as any)?.firstname, (row as any)?.lastname]
        .filter(Boolean)
        .join(" ") || "-",
  },
  {
    key: "glassbatch",
    label: "Glasbatch",
    render: (value, _row) => (value ? String(value) : "-"),
  },
  {
    key: "productiondate",
    label: "Glas Produktionsdatum",
    render: (value, _row) =>
      value ? format(new Date(value as string), "yyyy-MM-dd") : "-",
  },
  {
    key: "name",
    label: "Glas Material",
    render: (value, _row) => (value ? String(value) : "-"),
  },
  {
    key: "ishardeningpass",
    label: "Glas ist gehärtet",
    render: (value, _row) => (value ? "Ja" : "Nein"),
  },
  {
    key: "ispaletteopened",
    label: "Palette ist offen",
    render: (value, _row) => (value ? "Ja" : "Nein"),
  },
];
